import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, ClipboardList, ExternalLink, Loader2, MessageCircle, Plus, Save, Ticket, UserPlus, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TICKET_STATUS_STYLES = {
  Open: 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-amber-100 text-amber-700',
  'Ready to Dispatch': 'bg-emerald-100 text-emerald-700',
  Cancelled: 'bg-red-100 text-red-700',
  Void: 'bg-slate-200 text-slate-600',
};
const TASK_STATUS_STYLES = {
  Locked: 'bg-slate-200 text-slate-600',
  Open: 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-amber-100 text-amber-700',
  Done: 'bg-green-100 text-green-700',
  Cancelled: 'bg-red-100 text-red-700',
};
const TASK_STATUSES = ['Open', 'In Progress', 'Done', 'Cancelled'];
const TASK_PRIORITIES = ['Low', 'Medium', 'High'];
const emptyTicketForm = { quotation_id: '', description: '', assigned_to: '' };
const emptyTaskForm = {
  title: '', description: '', status: 'Open', assigned_to: '', due_date: '', priority: 'Medium',
};
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const formatDate = value => value
  ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  : '-';

export const LeadTicketsSection = ({ leadId, customerName, customerPhone, quotations, serviceUsers, canEdit }) => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [tasksByTicket, setTasksByTicket] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketForm, setTicketForm] = useState(emptyTicketForm);
  const [ticketSaving, setTicketSaving] = useState(false);
  const [taskTicket, setTaskTicket] = useState(null);
  const [taskForm, setTaskForm] = useState(emptyTaskForm);
  const [taskSaving, setTaskSaving] = useState(false);
  const [systemTasksSaving, setSystemTasksSaving] = useState(false);
  const [sectionSuccess, setSectionSuccess] = useState('');
  const [assignTask, setAssignTask] = useState(null);
  const [assignUserId, setAssignUserId] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);
  const [notificationTask, setNotificationTask] = useState(null);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [statusSavingTaskId, setStatusSavingTaskId] = useState(null);

  const refreshTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const ticketResponse = await axios.get(
        `${BACKEND_URL}/api/tickets/by-lead/${leadId}`,
        getAuthHeaders(),
      );
      const leadTickets = ticketResponse.data || [];
      const taskResponses = await Promise.all(leadTickets.map(async ticket => {
        try {
          const response = await axios.get(
            `${BACKEND_URL}/api/tasks/by-ticket/${ticket.id}`,
            getAuthHeaders(),
          );
          return [ticket.id, response.data || []];
        } catch {
          return [ticket.id, []];
        }
      }));
      setTickets(leadTickets);
      setTasksByTicket(Object.fromEntries(taskResponses));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    refreshTickets();
  }, [refreshTickets, quotations]);

  const openTicketModal = () => {
    setTicketForm({ ...emptyTicketForm });
    setError('');
    setTicketModalOpen(true);
  };

  const addSystemTasks = async () => {
    setSystemTasksSaving(true);
    setError('');
    setSectionSuccess('');
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/tickets/system-tasks/${leadId}`,
        {},
        getAuthHeaders(),
      );
      setSectionSuccess(`Tasks added to ${response.data.ticket_number}.`);
      await refreshTickets();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add tasks');
    } finally {
      setSystemTasksSaving(false);
    }
  };

  const createTicket = async event => {
    event.preventDefault();
    setTicketSaving(true);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/tickets`, {
        lead_id: leadId,
        quotation_id: ticketForm.quotation_id || null,
        description: ticketForm.description.trim() || null,
        assigned_to: ticketForm.assigned_to || null,
        status: 'Open',
      }, getAuthHeaders());
      setTicketModalOpen(false);
      await refreshTickets();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setTicketSaving(false);
    }
  };

  const openTaskModal = ticket => {
    setTaskTicket(ticket);
    setTaskForm({ ...emptyTaskForm, assigned_to: ticket.assigned_to || '' });
    setError('');
  };

  const createTask = async event => {
    event.preventDefault();
    if (!taskForm.title.trim()) {
      setError('Task title is required');
      return;
    }
    setTaskSaving(true);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/tasks`, {
        ticket_id: taskTicket.id,
        title: taskForm.title.trim(),
        description: taskForm.description.trim() || null,
        status: taskForm.status,
        assigned_to: taskForm.assigned_to || null,
        due_date: taskForm.due_date || null,
        priority: taskForm.priority,
      }, getAuthHeaders());
      setTaskTicket(null);
      await refreshTickets();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create task');
    } finally {
      setTaskSaving(false);
    }
  };

  const openAssignModal = task => {
    setAssignTask(task);
    setAssignUserId('');
    setError('');
  };

  const assignSelectedTask = async event => {
    event.preventDefault();
    if (!assignUserId) {
      setError('Select a service technician or service manager.');
      return;
    }
    setAssignSaving(true);
    setError('');
    try {
      await axios.put(
        `${BACKEND_URL}/api/tasks/${assignTask.id}`,
        { assigned_to: assignUserId },
        getAuthHeaders(),
      );
      setAssignTask(null);
      setAssignUserId('');
      await refreshTickets();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to assign task');
    } finally {
      setAssignSaving(false);
    }
  };

  const openNotificationModal = task => {
    setNotificationTask(task);
    setNotificationMessage(task.communication_messages?.[0] || '');
  };

  const notifyCustomer = () => {
    const digits = String(customerPhone || '').replace(/\D/g, '');
    const whatsappNumber = digits.length === 10 ? `91${digits}` : digits;
    if (!whatsappNumber || !notificationMessage) return;
    window.open(
      `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(notificationMessage)}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  const updateTaskStatus = async (task, status) => {
    if (status === task.status) return;
    setStatusSavingTaskId(task.id);
    setError('');
    setSectionSuccess('');
    try {
      await axios.put(
        `${BACKEND_URL}/api/tasks/${task.id}`,
        { status },
        getAuthHeaders(),
      );
      await refreshTickets();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update task status');
    } finally {
      setStatusSavingTaskId(null);
    }
  };

  return (
    <>
      <section className="bg-white rounded-sm border border-slate-200 p-6 mb-6" data-testid="lead-tickets">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Tasks</h2>
          {canEdit && (
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={addSystemTasks} disabled={systemTasksSaving}
                data-testid="add-system-tasks-btn"
                className="inline-flex items-center justify-center gap-2 border border-orange-500 text-orange-600 font-bold uppercase tracking-wider px-4 py-2 rounded-sm hover:bg-orange-50 transition-colors disabled:opacity-50">
                {systemTasksSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardList className="h-4 w-4" />}
                Add Tasks
              </button>
              <button type="button" onClick={openTicketModal} data-testid="create-lead-ticket-btn"
                className="inline-flex items-center justify-center gap-2 bg-orange-500 text-white font-bold uppercase tracking-wider px-4 py-2 rounded-sm hover:bg-orange-600 transition-colors">
                <Ticket className="h-4 w-4" /> Create Ticket
              </button>
            </div>
          )}
        </div>

        {sectionSuccess && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-sm mb-4">
            {sectionSuccess}
          </div>
        )}
        {error && !ticketModalOpen && !taskTicket && !assignTask && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4">
            <AlertCircle className="h-4 w-4 shrink-0" /><span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>
        ) : tickets.length === 0 ? (
          <p className="text-sm text-slate-500">N/A</p>
        ) : (
          <div className="space-y-4">
            {tickets.map(ticket => {
              const tasks = tasksByTicket[ticket.id] || [];
              return (
                <div key={ticket.id} className="border border-slate-200 rounded-sm" data-testid={`lead-ticket-${ticket.id}`}>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 bg-slate-50 border-b border-slate-200">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-bold text-sm text-slate-900">{ticket.ticket_number}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${TICKET_STATUS_STYLES[ticket.status] || 'bg-slate-100 text-slate-600'}`}>
                          {ticket.status}
                        </span>
                        {ticket.quotation_number && <span className="text-xs text-slate-500">{ticket.quotation_number}</span>}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {ticket.assigned_to_name ? `Assigned to ${ticket.assigned_to_name}` : 'Unassigned'} · {formatDate(ticket.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {canEdit && (
                        <button type="button" onClick={() => openTaskModal(ticket)}
                          data-testid={`add-lead-ticket-task-${ticket.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-2 bg-orange-500 text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600">
                          <Plus className="h-3.5 w-3.5" /> Add Task
                        </button>
                      )}
                      <button type="button" onClick={() => navigate(`/admin/services/tickets/${ticket.id}`)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-200 bg-white text-slate-700 text-xs font-medium rounded-sm hover:border-orange-300 hover:text-orange-600">
                        Ticket Details <ExternalLink className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="p-4">
                    {tasks.length === 0 ? (
                      <p className="text-sm text-slate-500">No tasks added.</p>
                    ) : (
                      <div className="space-y-2">
                        {tasks.map(task => (
                          <div key={task.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border border-slate-100 px-3 py-2.5 rounded-sm">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{task.title}</p>
                              <p className="text-xs text-slate-500">
                                {task.assigned_to_name ? `Assigned to ${task.assigned_to_name}` : 'Unassigned'}
                                {task.due_date ? ` · Due ${formatDate(task.due_date)}` : ''}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {canEdit ? (
                                <div className="relative">
                                  <select value={task.status}
                                    onChange={event => updateTaskStatus(task, event.target.value)}
                                    disabled={task.status === 'Locked' || statusSavingTaskId === task.id}
                                    data-testid={`lead-task-status-${task.id}`}
                                    className={`appearance-none pl-2.5 pr-7 py-1.5 rounded text-xs font-semibold border-0 focus:outline-none disabled:cursor-not-allowed ${TASK_STATUS_STYLES[task.status] || 'bg-slate-100 text-slate-600'}`}>
                                    {task.status === 'Locked' && <option value="Locked">Locked</option>}
                                    {TASK_STATUSES.map(status => <option key={status} value={status}>{status}</option>)}
                                  </select>
                                  {statusSavingTaskId === task.id && (
                                    <Loader2 className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin" />
                                  )}
                                </div>
                              ) : (
                                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${TASK_STATUS_STYLES[task.status] || 'bg-slate-100 text-slate-600'}`}>{task.status}</span>
                              )}
                              <span className="text-xs text-slate-500">{task.priority}</span>
                              {canEdit && !task.assigned_to && (
                                <button type="button" onClick={() => openAssignModal(task)}
                                  data-testid={`assign-task-${task.id}`}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-orange-300 text-orange-600 text-xs font-semibold rounded-sm hover:bg-orange-50">
                                  <UserPlus className="h-3.5 w-3.5" /> Assign
                                </button>
                              )}
                              <button type="button" onClick={() => openNotificationModal(task)}
                                data-testid={`notify-customer-${task.id}`}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-green-300 text-green-700 text-xs font-semibold rounded-sm hover:bg-green-50">
                                <MessageCircle className="h-3.5 w-3.5" /> Notify Customer
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {ticketModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="create-lead-ticket-modal">
          <div className="bg-white rounded-sm w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">Create Ticket</h2>
                <p className="text-sm text-slate-500 mt-1">Create a ticket linked to this lead.</p>
              </div>
              <button type="button" onClick={() => setTicketModalOpen(false)} disabled={ticketSaving} className="p-2 text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={createTicket} className="p-6">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4">
                  <AlertCircle className="h-4 w-4 shrink-0" /><span>{error}</span>
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Quotation</label>
                  <select value={ticketForm.quotation_id}
                    onChange={event => setTicketForm(current => ({ ...current, quotation_id: event.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm bg-white focus:border-orange-500 focus:outline-none">
                    <option value="">No quotation</option>
                    {(quotations || []).filter(quotation => (
                      !tickets.some(ticket => ticket.quotation_id === quotation.id)
                    )).map(quotation => (
                      <option key={quotation.id} value={quotation.id}>{quotation.quotation_number} ({quotation.status})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Assigned To</label>
                  <select value={ticketForm.assigned_to}
                    onChange={event => setTicketForm(current => ({ ...current, assigned_to: event.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm bg-white focus:border-orange-500 focus:outline-none">
                    <option value="">Unassigned</option>
                    {serviceUsers.map(user => <option key={user.id} value={user.id}>{user.name} ({user.role_name})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea rows={3} value={ticketForm.description}
                    onChange={event => setTicketForm(current => ({ ...current, description: event.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none" />
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button type="button" onClick={() => setTicketModalOpen(false)} disabled={ticketSaving}
                  className="px-5 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={ticketSaving}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50">
                  {ticketSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ticket className="h-4 w-4" />} Create Ticket
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {taskTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto" data-testid="create-lead-task-modal">
          <div className="bg-white rounded-sm w-full max-w-2xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">Add Task</h2>
                <p className="text-sm text-slate-500 mt-1">Task for {taskTicket.ticket_number}</p>
              </div>
              <button type="button" onClick={() => setTaskTicket(null)} disabled={taskSaving} className="p-2 text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={createTask} className="p-6">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4">
                  <AlertCircle className="h-4 w-4 shrink-0" /><span>{error}</span>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Title <span className="text-red-500">*</span></label>
                  <input type="text" value={taskForm.title} required
                    onChange={event => setTaskForm(current => ({ ...current, title: event.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea rows={2} value={taskForm.description}
                    onChange={event => setTaskForm(current => ({ ...current, description: event.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                  <select value={taskForm.status} onChange={event => setTaskForm(current => ({ ...current, status: event.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm bg-white focus:border-orange-500 focus:outline-none">
                    {TASK_STATUSES.map(status => <option key={status} value={status}>{status}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                  <select value={taskForm.priority} onChange={event => setTaskForm(current => ({ ...current, priority: event.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm bg-white focus:border-orange-500 focus:outline-none">
                    {TASK_PRIORITIES.map(priority => <option key={priority} value={priority}>{priority}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Assigned To</label>
                  <select value={taskForm.assigned_to} onChange={event => setTaskForm(current => ({ ...current, assigned_to: event.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm bg-white focus:border-orange-500 focus:outline-none">
                    <option value="">Unassigned</option>
                    {serviceUsers.map(user => <option key={user.id} value={user.id}>{user.name} ({user.role_name})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
                  <input type="date" value={taskForm.due_date}
                    onChange={event => setTaskForm(current => ({ ...current, due_date: event.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button type="button" onClick={() => setTaskTicket(null)} disabled={taskSaving}
                  className="px-5 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={taskSaving}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50">
                  {taskSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Add Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {assignTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="assign-lead-task-modal">
          <div className="bg-white rounded-sm w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">Assign Task</h2>
                <p className="text-sm text-slate-500 mt-1">{assignTask.title}</p>
              </div>
              <button type="button" onClick={() => setAssignTask(null)} disabled={assignSaving} className="p-2 text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={assignSelectedTask} className="p-6">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4">
                  <AlertCircle className="h-4 w-4 shrink-0" /><span>{error}</span>
                </div>
              )}
              <label className="block text-sm font-medium text-slate-700 mb-1">Assign To <span className="text-red-500">*</span></label>
              <select value={assignUserId} onChange={event => setAssignUserId(event.target.value)} required
                className="w-full px-3 py-2 border border-slate-200 rounded-sm bg-white focus:border-orange-500 focus:outline-none">
                <option value="">Select service technician or manager</option>
                {serviceUsers.map(user => <option key={user.id} value={user.id}>{user.name} ({user.role_name})</option>)}
              </select>
              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button type="button" onClick={() => setAssignTask(null)} disabled={assignSaving}
                  className="px-5 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={assignSaving || !assignUserId}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50">
                  {assignSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Assign
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {notificationTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="notify-customer-modal">
          <div className="bg-white rounded-sm w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">Notify Customer</h2>
                <p className="text-sm text-slate-500 mt-1">{notificationTask.title}</p>
              </div>
              <button type="button" onClick={() => setNotificationTask(null)} className="p-2 text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="rounded-sm border border-slate-200 bg-slate-50 p-4 space-y-4 md:col-span-1">
                  <div>
                    <span className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Customer Name</span>
                    <span className="text-sm font-medium text-slate-900">{customerName || '-'}</span>
                  </div>
                  <div>
                    <span className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Phone Number</span>
                    <span className="text-sm font-medium text-slate-900">{customerPhone || '-'}</span>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <label className="block text-sm font-medium text-slate-700">Choose a Default Message</label>
                    <button type="button" onClick={() => setNotificationMessage('')}
                      className="text-xs font-semibold text-orange-600 hover:text-orange-700">
                      Type Custom Message
                    </button>
                  </div>
                  {notificationTask.communication_messages?.length ? (
                    <div className="space-y-2 max-h-56 overflow-y-auto pr-1" data-testid="notification-default-messages">
                      {notificationTask.communication_messages.map((message, index) => (
                        <button type="button" key={`${index}-${message}`}
                          onClick={() => setNotificationMessage(message)}
                          className={`w-full text-left whitespace-pre-wrap rounded-sm border p-3 text-sm transition-colors ${
                            notificationMessage === message
                              ? 'border-orange-400 bg-orange-50 text-orange-900'
                              : 'border-slate-200 bg-white text-slate-700 hover:border-orange-300 hover:bg-orange-50/50'
                          }`}>
                          {message}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                      No default communication message is configured. You can type a custom message below.
                    </div>
                  )}
                  <label className="block text-sm font-medium text-slate-700 mt-5 mb-1">Message</label>
                  <textarea value={notificationMessage}
                    onChange={event => setNotificationMessage(event.target.value)}
                    rows={7} placeholder="Select a default message above or type your own message here..."
                    data-testid="notification-message-input"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-y" />
                  <p className="text-xs text-slate-500 mt-1">The message is editable before opening WhatsApp.</p>
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button type="button" onClick={() => setNotificationTask(null)}
                  className="px-5 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="button" onClick={notifyCustomer}
                  disabled={!customerPhone || !notificationMessage}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-green-600 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-green-700 disabled:opacity-50">
                  <MessageCircle className="h-4 w-4" /> Open WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
